import { format, parseISO } from "date-fns";

import type {
  DepositGrowthPoint,
  MyDealRow,
  ProfitDynamicsPoint,
} from "@/components/analytics/types";
import type { AggregatedDealRow, AnalyticsTimeSeriesPoint } from "@/types/statistics";

/**
 * Адаптеры «сырые данные бэкенда → пропсы компонентов аналитики».
 *
 * Сервер уже выполняет агрегацию по календарным периодам. Здесь остаётся только
 * преобразование названий полей в view-model компонентов.
 */

/** Гранулярность бакетов столбчатого графика. */
export type ProfitBucket = "hour" | "day" | "month";

const BUCKET_LABEL_FORMAT: Record<ProfitBucket, string> = {
  hour: "HH:mm",
  day: "dd.MM",
  month: "LLL",
};

export const groupTimeSeriesByDays = (
  series: AnalyticsTimeSeriesPoint[],
  days: number
): AnalyticsTimeSeriesPoint[] => {
  const groups = new Map<string, AnalyticsTimeSeriesPoint>();

  for (const point of [...series].sort((a, b) => a.date.localeCompare(b.date))) {
    const [year, month, day] = point.date.slice(0, 10).split("-");
    const groupDay = Math.floor((Number(day) - 1) / days) * days + 1;
    const date = `${year}-${month}-${String(groupDay).padStart(2, "0")}`;
    const current = groups.get(date);

    if (current) {
      current.profit_usdt += point.profit_usdt;
      current.dirty_profit_usdt += point.dirty_profit_usdt;
      current.commission_usdt += point.commission_usdt;
      current.trades_count += point.trades_count;
      current.capital_usdt = point.capital_usdt ?? current.capital_usdt;
      current.used_fuel_usdt += point.used_fuel_usdt;
      current.cumulative_pnl_usdt = point.cumulative_pnl_usdt;
    } else {
      groups.set(date, {
        date,
        profit_usdt: point.profit_usdt,
        dirty_profit_usdt: point.dirty_profit_usdt,
        commission_usdt: point.commission_usdt,
        trades_count: point.trades_count,
        capital_usdt: point.capital_usdt,
        used_fuel_usdt: point.used_fuel_usdt,
        cumulative_pnl_usdt: point.cumulative_pnl_usdt,
      });
    }
  }

  return [...groups.values()];
};

/** Дата закрытия сделки; для незакрытых — дата входа. */
const dealDate = (deal: AggregatedDealRow): Date => parseISO(deal.exit_time ?? deal.entry_time);

/** Сумма сделки, от которой считается процент профита. */
const dealNotional = (deal: AggregatedDealRow): number => deal.notional_usdt ?? 0;

const dealId = (deal: AggregatedDealRow, index: number): string => {
  if (deal.kind === "global_cycle") return deal.global_cycle_id;
  if (deal.kind === "hedge_pair") return deal.pair_cycle_id;
  return deal.position_id ?? `${deal.instrument_id}-${deal.entry_time}-${index}`;
};

/**
 * Столбчатый график «Динамика прибыли» из готовых server-side buckets.
 */
export const toProfitDynamics = (
  series: AnalyticsTimeSeriesPoint[],
  bucket: ProfitBucket = "month"
): ProfitDynamicsPoint[] => {
  const points = [...series]
    .sort((a, b) => a.date.localeCompare(b.date))
    .map((point) => ({
      label: format(parseISO(point.date), BUCKET_LABEL_FORMAT[bucket]),
      profit: point.profit_usdt,
      fuel: point.used_fuel_usdt,
      pnl: point.dirty_profit_usdt,
    }));

  return points;
};

/**
 * Линейный график «Рост депозита» — одна точка на готовый server-side bucket.
 *   Баланс OKX → фактический капитал на бирже
 *   Чистый PnL → накопленная прибыль сервиса за вычетом fuel
 */
export const toDepositGrowth = (
  series: AnalyticsTimeSeriesPoint[],
  bucket: ProfitBucket = "month"
): DepositGrowthPoint[] => {
  const sorted = [...series].sort((a, b) => a.date.localeCompare(b.date));
  let latestCapital: number | null = null;
  const points: DepositGrowthPoint[] = [];

  for (const point of sorted) {
    latestCapital = point.capital_usdt ?? latestCapital;
    if (latestCapital === null) continue;

    points.push({
      label: format(parseISO(point.date), BUCKET_LABEL_FORMAT[bucket]),
      capital: latestCapital,
      pnl: point.cumulative_pnl_usdt,
    });
  }

  return points;
};

export const summarizeTimeSeries = (series: AnalyticsTimeSeriesPoint[]) => {
  const { profit, fuel } = series.reduce(
    (totals, point) => ({
      profit: totals.profit + point.profit_usdt,
      fuel: totals.fuel + point.used_fuel_usdt,
    }),
    { profit: 0, fuel: 0 }
  );
  const capital = series.find((point) => point.capital_usdt != null)?.capital_usdt ?? 0;

  return {
    profit,
    fuel,
    profitPercent: capital > 0 ? (profit / capital) * 100 : 0,
  };
};

export const calculateCapitalChange = (
  currentCapital: number,
  series: AnalyticsTimeSeriesPoint[]
): number => {
  const startCapital = series.find((point) => point.capital_usdt != null)?.capital_usdt;
  return startCapital && startCapital > 0
    ? ((currentCapital - startCapital) / startCapital) * 100
    : 0;
};

export const toAccountEventStatus = (status: string): string =>
  status === "completed" ? "Выполнено" : status === "accepted" ? "Принято" : status;

/** Таблица «Мои сделки» — свежие сверху. */
export const toMyDealRows = (deals: AggregatedDealRow[]): MyDealRow[] =>
  [...deals]
    .sort((a, b) => dealDate(b).getTime() - dealDate(a).getTime())
    .map((deal, index) => {
      const date = dealDate(deal);
      const notional = dealNotional(deal);
      const profitUsdt = deal.profit_usdt ?? 0;

      return {
        id: dealId(deal, index),
        date: format(date, "dd.MM.yyyy"),
        symbol: deal.instrument_id,
        time: format(date, "HH:mm:ss"),
        profitUsdt,
        profitPercent: notional > 0 ? (profitUsdt / notional) * 100 : 0,
        usedFuel: deal.used_fuel ?? 0,
      };
    });
